import InputLabel from '@mui/material/InputLabel'
import { SxProps } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { toCommaDecString } from '@shared/conversions'
import { NumberFormatEnum } from '@shared/SchemaTypes'

import React, { KeyboardEvent, useCallback } from 'react'

// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export default function OtAdminNumberField(props: {
  id: string
  inputLabel: string
  value: string
  sx: SxProps
  variant: 'outlined' | 'standard' | 'filled'
  onChange: (value: string) => void
  error: boolean | undefined
  helperText: string | undefined
  numberFormat: NumberFormatEnum
}): React.ReactElement {
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.']

    if ((e.metaKey || e.ctrlKey) && ['c', 'v', 'x', 'a'].includes(e.key.toLowerCase())) {
      return
    }

    // Only allow digits
    if (/^\d$/.test(e.key)) return

    // Allow control/navigation keys and one dot or minus
    if (allowed.includes(e.key)) {
      const input = e.currentTarget

      if (e.key === '.' && input.value && input.value.includes('.')) e.preventDefault()
      if (e.key === '-' && input.selectionStart !== 0) e.preventDefault()

      return
    }

    e.preventDefault()
  }, [])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    let v = event.target.value

    // Convert comma → dot for en-GB consistency
    if (v.includes(',')) {
      v = v.replace(/,/g, '.')
    }

    // Remove anything not valid for en-GB numeric entry
    v = v.replace(/[^0-9.-]/g, '')

    // Prevent multiple dots
    const firstDot = v.indexOf('.')
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '') // remove any extra dots
    }

    // Prevent multiple minus signs
    if (v.indexOf('-') > 0) {
      v = v.replace(/-/g, '')
      v = '-' + v
    }

    event.target.value = v
  }, [])

  const formattedValue =
    props.numberFormat === NumberFormatEnum.ERC20 ? toCommaDecString(props.value.toString()) : undefined

  return (
    <>
      <InputLabel htmlFor={props.id}>{props.inputLabel}</InputLabel>
      <TextField
        id={props.id}
        inputProps={{
          'data-testid': props.id,
        }}
        InputProps={{
          endAdornment: props.numberFormat === NumberFormatEnum.APR ? '%' : undefined,
        }}
        sx={props.sx}
        variant={props.variant}
        placeholder="Enter Exchange Rate (Decimal Form)"
        value={props.value}
        type={'text'}
        onKeyDown={handleKeyDown}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
          handleChange(event)
          props.onChange(event.target.value)
        }}
        error={props.error}
        helperText={props.helperText ?? formattedValue}
      />
    </>
  )
}
